/**
 * SIDEBAR STORE
 *
 * Manages sidebar UI state: collapsed/expanded, projects section, sub-tab selection.
 * Persists collapsed state to localStorage.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ProjectSubTab =
  | "overview"
  | "board"
  | "git"
  | "terminal"
  | "chat"
  | "secrets"
  | "documents";

interface SidebarState {
  isCollapsed: boolean;
  projectsExpanded: boolean;
  selectedProjectSubTab: ProjectSubTab;
  projectOrder: number[];
  reorderingProjects: boolean;

  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleProjectsExpanded: () => void;
  setProjectsExpanded: (expanded: boolean) => void;
  setSelectedProjectSubTab: (tab: ProjectSubTab) => void;
  setProjectOrder: (order: number[]) => void;
  setReorderingProjects: (reordering: boolean) => void;
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      isCollapsed: false,
      projectsExpanded: true,
      selectedProjectSubTab: "overview",
      projectOrder: [],
      reorderingProjects: false,

      toggleCollapsed: () => set((s) => ({ isCollapsed: !s.isCollapsed })),
      setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
      toggleProjectsExpanded: () =>
        set((s) => ({ projectsExpanded: !s.projectsExpanded })),
      setProjectsExpanded: (expanded) => set({ projectsExpanded: expanded }),
      setSelectedProjectSubTab: (tab) => set({ selectedProjectSubTab: tab }),
      setProjectOrder: (order) => set({ projectOrder: order }),
      setReorderingProjects: (reordering) => set({ reorderingProjects: reordering }),
    }),
    {
      name: "colony-sidebar",
      version: 1,
      partialize: (state) => ({
        isCollapsed: state.isCollapsed,
        projectsExpanded: state.projectsExpanded,
        selectedProjectSubTab: state.selectedProjectSubTab,
        projectOrder: state.projectOrder,
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<SidebarState>) };
        // Fallback: if persisted tab was removed ("list" or "settings"), reset to "overview"
        const tab = merged.selectedProjectSubTab as string;
        if (tab === "list" || tab === "settings") {
          merged.selectedProjectSubTab = "overview";
        }
        return merged;
      },
    },
  ),
);
